/**
 * The board as a picture, for filing or for keeping.
 *
 * A serialised SVG travels alone: no stylesheet, no custom properties, none of
 * the CSS that gives the board its colours and type sizes. Rasterised as it
 * stands, the drawing came out black-on-white with the therapist's own colours
 * gone and the collaboration chrome — the chips saying who last touched what —
 * left behind as black lozenges.
 *
 * So the copy is prepared rather than serialised: chrome removed, and every
 * painted property read from the live board and written onto the copy as
 * inline style. What comes out is what was on screen.
 */

/** Properties that decide how a shape is painted, read from the live board. */
const PAINTED = [
    "color",
    "fill",
    "fill-opacity",
    "stroke",
    "stroke-width",
    "stroke-dasharray",
    "stroke-linecap",
    "opacity",
    "font-size",
    "font-weight",
    "font-family",
    "font-style",
] as const;

/** Anything the board draws for the people using it, not for the record. */
const CHROME = "[data-board-chrome]";

const paintOnto = (live: Element, copy: Element) => {
    const computed = window.getComputedStyle(live);
    const declared = PAINTED.map((property) => {
        const value = computed.getPropertyValue(property);
        return value ? `${property}:${value}` : "";
    })
        .filter(Boolean)
        .join(";");

    if (declared) copy.setAttribute("style", declared);

    const liveChildren = live.children;
    const copyChildren = copy.children;
    for (let index = 0; index < liveChildren.length; index += 1) {
        const copyChild = copyChildren[index];
        if (copyChild) paintOnto(liveChildren[index], copyChild);
    }
};

/**
 * A standalone copy of the board, sized and painted to survive on its own.
 *
 * The size has to be said out loud: the board's SVG carries a viewBox and no
 * width or height — it fills whatever box CSS gives it — and an SVG like that,
 * loaded as an image, falls back to the browser's 300x150 default.
 */
export const boardSvgCopy = (
    svg: SVGSVGElement,
    longEdge: number,
): { copy: SVGSVGElement; width: number; height: number } => {
    const [, , boxWidth, boxHeight] = (svg.getAttribute("viewBox") ?? "")
        .split(/\s+/)
        .map(Number);
    const frame = svg.getBoundingClientRect();
    const ratio =
        boxWidth && boxHeight
            ? boxWidth / boxHeight
            : (frame.width || 4) / (frame.height || 3);

    const width = Math.round(ratio >= 1 ? longEdge : longEdge * ratio);
    const height = Math.round(ratio >= 1 ? longEdge / ratio : longEdge);

    const copy = svg.cloneNode(true) as SVGSVGElement;
    paintOnto(svg, copy);
    copy.querySelectorAll(CHROME).forEach((element) => element.remove());
    copy.setAttribute("width", String(width));
    copy.setAttribute("height", String(height));
    copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    return { copy, width, height };
};

/** Rasterise the board onto a white ground. */
export const boardToPng = async (
    svg: SVGSVGElement,
    longEdge: number,
): Promise<Blob | null> => {
    const { copy, width, height } = boardSvgCopy(svg, longEdge);
    const serialized = new XMLSerializer().serializeToString(copy);
    const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;

    const image = new Image();
    await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error("snapshot"));
        image.src = source;
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    // White, not transparent: a genogram printed from a transparent PNG comes
    // out as lines on whatever is behind them.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
};
